import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import AttentionNeededIcon from "../Images/AttentionNeeded.png"
import { RenderProfitCenterMultiple } from "../HelperFunctions/MultiplePopUpTable";

/**
 * PayPeriodTable (PayrollEmployeeListTable)
 * Renders the pay period employee list with columns, totals, status badges, and profit center popups.
 */
const PayPeriodTable = ({ rows, columns, showTotals = true, onRowClick, customTotals, isHoursDecimal }) => {
  const totalFields = [
    "hrs",
    "grossPay",
    "additions",
    "deductions",
    "eeTaxes",
    "netPay",
    "erTaxes",
  ];

  const centerFields = [
    "hrs",
    "grossPay",
    "additions",
    "deductions",
    "eeTaxes",
    "netPay",
    "erTaxes",
    "view",
  ];

  const parseCurrency = (value) => {
    if (typeof value === "string") {
      return parseFloat(value.replace(/,/g, "")) || 0;
    }
    return value || 0;
  };

  const formatHours = (val) => {
    if (val === null || val === undefined || val === "") return isHoursDecimal ? "0.00" : "0:00";
    if (typeof val === "string" && val.includes(":")) {
      if (isHoursDecimal) {
        const [h, m] = val.split(':');
        const hours = parseInt(h, 10) || 0;
        const minutes = parseInt(m, 10) || 0;
        const isNegative = hours < 0 || (hours === 0 && val.startsWith('-'));
        const decimal = Math.abs(hours) + (minutes / 60);
        const finalNum = isNegative ? -decimal : decimal;
        return finalNum.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      return val;
    }

    if (isHoursDecimal) {
      const num = parseCurrency(val);
      return num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    const decimalHours = parseCurrency(val);
    const isNegative = decimalHours < 0;
    const absHours = Math.abs(decimalHours);
    let hours = Math.floor(absHours);
    let minutes = Math.round((absHours - hours) * 60);
    if (minutes === 60) {
      hours += 1;
      minutes = 0;
    }
    const paddedMinutes = minutes.toString().padStart(2, "0");
    return `${isNegative ? "-" : ""}${hours}:${paddedMinutes}`;
  };

  const calculateTotal = (data, field) => {
    if (customTotals && customTotals[field] !== undefined) {
      if (field === "hrs") return formatHours(customTotals[field]);
      return parseCurrency(customTotals[field]).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return field === "hrs" ? formatHours(0) : (0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <TableContainer component={Paper} elevation={0} sx={{ 
      height: "100%", 
      p: 0, 
      border: "1px solid #f5f5f5", 
      borderRadius: 1,
      overflow: "auto",
      "&::-webkit-scrollbar": { width: 5, height: 5 },
      "&::-webkit-scrollbar-thumb": { backgroundColor: "#bdbdbd", borderRadius: 10 }
    }}>
      <Table
        size="small"
        stickyHeader
        // sx={{ width: "100%" }}
      >
        <TableHead 
        // sx={{ position: "sticky", top: 0, zIndex: 5 }}
        >
          <TableRow>
            {columns.map((col, idx) => (
              <TableCell
                key={idx}
                sx={{
                  // position: "sticky",
                  // top: 0,
                  // zIndex: 5,
                  background: "linear-gradient(to bottom, #e6e6e6 0%, #f2f2f2 50%, #e6e6e6 100%)",
                  borderTop: "1px solid #f2f2f2",
                  borderBottom: "1px solid #f2f2f2",
                  borderRight: columns[idx + 1]?.field === "voidStar" ? "none" : "1px solid #f2f2f2",
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#000",
                  padding: "6px 8px",
                  whiteSpace: "nowrap",
                  textAlign: centerFields.includes(col.field) ? "center" : "left",
                }}
              >
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, rowIndex) => (
            // <TableRow
            //   hover
            //   key={rowIndex}
            //   sx={{
            //     cursor: onRowClick ? "pointer" : "default",
            //     "&:hover": { backgroundColor: "#f8f9fa" }
            //   }}
            //   onClick={() => {
            //     if (onRowClick) onRowClick(row);
            //   }}
            // >
            <TableRow
              hover
              key={rowIndex}
              sx={{
                "&:hover": { backgroundColor: "#f8f9fa" }
              }}
            >
              {columns.map((col, idx) => {
                const val = row[col.field];
                const isAttention = val === "Unmapped" || val === "AttentionNeeded";

                return (
                  <TableCell
                    key={idx}
                    sx={{
                      borderBottom: "1px solid #f2f2f2",
                      borderRight: columns[idx + 1]?.field === "voidStar" ? "none" : "1px solid #f2f2f2",
                      fontSize: 12,
                      fontWeight: 500,
                      color: "#626262",
                      padding: col.field === "voidStar" ? "10px 8px 10px 0" : "10px 8px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      textAlign: centerFields.includes(col.field) ? "center" : "left",
                    }}
                  >
                    {col.field === "voidStar" ? (
                      row.isVoided ? (
                        <Box sx={{ display: "flex", alignItems: "center", color: "#626262" }}>
                          <StarOutlineIcon sx={{ fontSize: 20 }} />
                        </Box>
                      ) : null
                    ) : isAttention ? (
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          onRowClick?.(row);
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          color: "#11bdf6",
                          cursor: "pointer",
                          justifyContent: centerFields.includes(col.field) ? "center" : "flex-start",
                        }}
                      >
                        <img src={AttentionNeededIcon} alt="Unmapped" width={18} height={18} />
                        Unmapped
                      </Box>
                    ) : val === "Matched" ? (
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            onRowClick?.(row);
                          }}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            color: "#11bdf6",
                            cursor: "pointer",
                            justifyContent: centerFields.includes(col.field) ? "center" : "flex-start",
                          }}
                        >
                          <VerifiedIcon fontSize="small" /> Matched
                        </Box>
                    ) : React.isValidElement(val) ? (
                      val
                    ) : col.field === "profitCenter" && String(val).trim().toLowerCase() === "multiple" ? (
                      <RenderProfitCenterMultiple pcNames={row?.profitCenterNames || row?.raw?.PCNames || row?.raw?.pcNames} />
                    ) : (
                      <span title={val}>
                        {col.field === "hrs"
                          ? formatHours(val)
                          : ["grossPay", "additions", "deductions", "eeTaxes", "netPay", "erTaxes"].includes(col.field)
                            ? parseCurrency(val).toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })
                            : val}
                      </span>
                    )}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
          {showTotals && (
            <TableRow sx={{ 
              position: "sticky", 
              bottom: 0, 
              zIndex: 2,
              boxShadow: "0px -2px 4px rgba(0,0,0,0.05)"
            }}>
              {columns.map((col, idx) => {
                if (idx === 0) {
                  return (
                    <TableCell
                      key={idx}
                      sx={{
                        position: "sticky",
                        bottom: 0,
                        zIndex: 2,
                        fontWeight: "bold",
                        fontSize: 12,
                        borderRight: "1px solid #f2f2f2",
                        p: 1,
                        textAlign: "center",
                        backgroundColor: "#f9f9f9"
                      }}
                    >
                      Total
                    </TableCell>
                  );
                }
                if (totalFields.includes(col.field)) {
                  return (
                    <TableCell
                      key={idx}
                      sx={{
                        position: "sticky",
                        bottom: 0,
                        zIndex: 2,
                        fontWeight: "bold",
                        fontSize: 12,
                        borderRight: "1px solid #f2f2f2",
                        p: 1,
                        textAlign: centerFields.includes(col.field) ? "center" : "left",
                        backgroundColor: "#f9f9f9"
                      }}
                    >
                      {calculateTotal(rows, col.field)}
                    </TableCell>
                  );
                }
                return (
                  <TableCell
                    key={idx}
                    sx={{ 
                      position: "sticky",
                      bottom: 0,
                      zIndex: 2,
                      borderRight: columns[idx + 1]?.field === "voidStar" ? "none" : "1px solid #f2f2f2",
                      backgroundColor: "#f9f9f9" 
                    }}
                  />
                );
              })}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PayPeriodTable;
